/**
 * Free Gift Orders — new tab on Bulk Order Approval (BulkOrderApprovalScm.js), visible
 * only to a hardcoded phone allowlist (mirrored on the backend, see
 * FREE_GIFT_ORDERS_ALLOWLIST in manijment-api controllers/sales.js).
 *
 * Lists the child BulkOrders that orderService.splitFreeGiftFromCapturedOrder creates
 * the moment a captured order contains a Product.freeGift item — payable = mrp x
 * quantity of the gift, paymentMethod 2, auto-captured/paid at split time. Each one
 * stays parked (isFreeGiftSplitOrder:true) until its parent order is delivered to at
 * least 80% of its payable value.
 *
 * "Sync Invoice" is not new business logic — it's the exact same two-step
 * createBulkSalesOrderOnUc -> shipBulkOrderFromUc pipeline any other bulk order goes
 * through (UC ship, Zoho inventory update, promotion into SplittedBulkOrders, real
 * Zoho invoice). This tab only gates *when* that's allowed to run (the 80%-delivered
 * check) and *who* can run it (the allowlist) — see Helper.syncFreeGiftOrderInvoice.
 */

import React, { useEffect, useState } from "react";
import axios from "axios";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  CircularProgress,
  TablePagination,
  Chip,
} from "@material-ui/core";

const BULK_PATH = process.env.REACT_APP_PATH_BULK;

const fmtMoney = (n) => (n == null ? "—" : "₹" + Math.round(n).toLocaleString("en-IN"));
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
    : "—";
const fmtAddress = (a) =>
  a ? [a.address1, a.address2, a.city, a.state, a.postalCode].filter(Boolean).join(", ") : "—";

const cellSx = { fontSize: 11, padding: "4px 6px", whiteSpace: "nowrap" };
const headSx = { ...cellSx, fontWeight: 700 };

const PARENT_STATUS_LABEL = {
  0: "In Cart",
  1: "Placed",
  2: "Cancelled",
  3: "Delivered",
  4: "RTO",
  5: "Pending",
  6: "Undelivered",
  7: "Shipped",
  8: "Split",
  9: "Lost/Damaged",
};

function FreeGiftOrderRow({ row, onSync, syncing }) {
  const [open, setOpen] = useState(false);
  const parent = row.parent;

  return (
    <React.Fragment>
      <TableRow>
        <TableCell style={cellSx}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell style={cellSx}>#{row.numericOrderId}</TableCell>
        <TableCell style={cellSx}>
          {parent ? `#${parent.numericOrderId}` : "—"}
        </TableCell>
        <TableCell style={cellSx}>{row.name || row.phoneNumber || "—"}</TableCell>
        <TableCell style={cellSx}>{row.phoneNumber || "—"}</TableCell>
        <TableCell align="right" style={cellSx}>{fmtMoney(row.totalPayableAmount)}</TableCell>
        <TableCell style={cellSx}>
          {parent ? (PARENT_STATUS_LABEL[parent.status] || parent.status) : "—"}
        </TableCell>
        <TableCell style={cellSx}>{fmtDate(row.createdAt)}</TableCell>
        <TableCell align="right" style={cellSx}>
          {row.status === 8 ? (
            <Chip label="Synced" size="small" style={{ fontSize: 10, height: 18, background: "#e8f5e9", color: "#1b5e20" }} />
          ) : (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!row.canSync || syncing}
              style={{ fontSize: 10, padding: "2px 8px", minWidth: 0 }}
              onClick={() => onSync(row)}
            >
              {syncing ? "Syncing…" : "Sync Invoice"}
            </Button>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0, borderBottom: open ? undefined : "none" }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box style={{ margin: "8px 16px", padding: 12, background: "#fafafa", border: "1px solid #eee", borderRadius: 4 }}>
              <Typography variant="caption" style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                Free gift product(s)
              </Typography>
              <Table size="small" style={{ marginBottom: 12 }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={headSx}>Product</TableCell>
                    <TableCell align="right" style={headSx}>Qty</TableCell>
                    <TableCell align="right" style={headSx}>MRP</TableCell>
                    <TableCell align="right" style={headSx}>Line total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(row.products || []).map((p, i) => (
                    <TableRow key={i}>
                      <TableCell style={cellSx}>{p.name}</TableCell>
                      <TableCell align="right" style={cellSx}>{p.quantity}</TableCell>
                      <TableCell align="right" style={cellSx}>{fmtMoney(p.mrp)}</TableCell>
                      <TableCell align="right" style={cellSx}>{fmtMoney((p.mrp || 0) * (p.quantity || 1))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography variant="caption" style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                Original (parent) order
              </Typography>
              {parent ? (
                <Box style={{ fontSize: 12, lineHeight: 1.8 }}>
                  <div>Order #{parent.numericOrderId} · {PARENT_STATUS_LABEL[parent.status] || parent.status}</div>
                  <div>Address: {fmtAddress(parent.address)}</div>
                  <div>
                    Sync eligibility: {row.canSync ? "≥80% of parent payable value delivered — ready to sync" : "not yet ≥80% delivered"}
                  </div>
                </Box>
              ) : (
                <Typography variant="caption" color="textSecondary">Parent order not found.</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function FreeGiftOrders() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [syncingId, setSyncingId] = useState(null);
  const [error, setError] = useState("");

  const loadOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${BULK_PATH}/sales/freeGiftOrders/list`, {
        page,
        pageLimit: pageSize,
      });
      if (res.data?.success) {
        setItems(res.data.data?.orders || []);
        setTotalCount(res.data.data?.totalCount || 0);
      } else {
        setError(res.data?.message || "Something went wrong");
        setItems([]);
        setTotalCount(0);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const onSync = async (row) => {
    setSyncingId(row._id);
    setError("");
    try {
      const res = await axios.post(`${BULK_PATH}/sales/freeGiftOrders/syncInvoice`, {
        orderId: row._id,
      });
      if (!res.data?.success) {
        setError(res.data?.message || "Sync failed");
      }
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        Free-gift child orders split off at capture time. "Sync Invoice" enables once the original order is
        delivered to at least 80% of its payable value — it runs the order through the normal Unicommerce +
        Zoho pipeline, same as any other bulk order.
      </Typography>

      {error && (
        <Typography variant="caption" style={{ color: "#b71c1c", display: "block", marginBottom: 10 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box style={{ padding: 30, textAlign: "center" }}><CircularProgress size={24} /></Box>
      ) : items.length === 0 ? (
        <Box style={{ padding: 30, textAlign: "center", color: "#888" }}>No free-gift split orders found.</Box>
      ) : (
        <Box style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ tableLayout: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell style={headSx} />
                <TableCell style={headSx}>Gift Order #</TableCell>
                <TableCell style={headSx}>Parent Order #</TableCell>
                <TableCell style={headSx}>Name</TableCell>
                <TableCell style={headSx}>Phone</TableCell>
                <TableCell align="right" style={headSx}>Payable</TableCell>
                <TableCell style={headSx}>Parent Status</TableCell>
                <TableCell style={headSx}>Created</TableCell>
                <TableCell align="right" style={headSx}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <FreeGiftOrderRow
                  key={row._id}
                  row={row}
                  onSync={onSync}
                  syncing={syncingId === row._id}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  );
}
